import { computed, defineComponent, inject, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import html_iway from "./template.iway.html?raw";
import { orderServiceKey } from "../../../infrastructure/gfo_api/order_service";
import { priceFormatterKey } from "../../../infrastructure/price_formatter";
import type { ProductVariantDto } from "../../../infrastructure/gfo_api/order_api";

const app_theme = import.meta.env.VITE_APP_THEME;
let html = "";
if ("iway" == app_theme) {
  html = html_iway;
} else if ("lic" == app_theme) {
  html = html_iway;
} else if ("sak" == app_theme) {
  html = html_iway;
}

const PRODUCT_ID_NUMBERS = 20;
const PRODUCT_ID_ADDITIONAL_NUMBER = 21;
const PRODUCT_ID_PORTED_NUMBER = 22;
const PRODUCT_ID_NEW_NUMBER = 27;

const sortByBaseVariantThenQtyMax = (a: ProductVariantDto, b: ProductVariantDto) => {
  if (a.qty_price_for == 0 && b.qty_price_for != 0) {
    return -1;
  }
  if (a.qty_price_for != 0 && b.qty_price_for == 0) {
    return 1;
  }
  return (a.qty_max ?? 0) - (b.qty_max ?? 0);
};

export default defineComponent({
  template: html,
  components: {},
  setup() {
    // Injections
    const { t } = useI18n();
    const orderService = inject(orderServiceKey);
    const priceFormatter = inject(priceFormatterKey);

    // variables and computed
    const numbersCartItem = computed(() => {
      return orderService?.currentItems?.value?.find(
        (it) => it.product_id == PRODUCT_ID_NUMBERS,
      ) ?? null;
    });
    const numbersVariant = computed(() => {
      const item = numbersCartItem.value;
      if (!item) {
        return null;
      }
      return orderService?.availableVariants?.value?.find(
        (v) => v.id == item.product_variant_id,
      ) ?? null;
    });
    const numbersIncluded = computed(() => numbersVariant.value?.numbers_included ?? 0);
    const additionalNumberQuantity = computed(() => {
      return orderService?.currentItems?.value?.reduce((sum, it) => {
        if (it.product_id == PRODUCT_ID_ADDITIONAL_NUMBER) {
          sum = sum + it.qty;
        }
        return sum;
      }, 0) ?? 0;
    });
    const totalNumbersQuantity = computed(() => numbersIncluded.value + additionalNumberQuantity.value);

    const newNumberVariant = computed(() => {
      return orderService?.availableVariants?.value?.find(
        (v) => v.product_id == PRODUCT_ID_NEW_NUMBER && v.qty_price_for == 0,
      ) ?? null;
    });
    const newNumberVariants = computed(() => {
      const variants = orderService?.availableVariants?.value?.filter(
        (v) => v.product_id == PRODUCT_ID_NEW_NUMBER,
      ) ?? [];
      return [...variants].sort(sortByBaseVariantThenQtyMax);
    });
    const portedNumberVariant = computed(() => {
      return orderService?.availableVariants?.value?.find(
        (v) => v.product_id == PRODUCT_ID_PORTED_NUMBER && v.qty_price_for == 0,
      ) ?? null;
    });
    const portedNumberVariants = computed(() => {
      const variants = orderService?.availableVariants?.value?.filter(
        (v) => v.product_id == PRODUCT_ID_PORTED_NUMBER,
      ) ?? [];
      return [...variants].sort(sortByBaseVariantThenQtyMax);
    });
    const currentPortedQuantity = computed(() => {
      return orderService?.currentItems?.value?.reduce((sum, it) => {
        if (it.product_id == PRODUCT_ID_PORTED_NUMBER) {
          sum = sum + it.qty;
        }
        return sum;
      }, 0) ?? 0;
    });

    const vatType = computed(() => {
      return orderService?.currentVatType.value;
    });

    // Schieber: der Wert entspricht der Anzahl portierter Nummern, der Rest sind neue Nummern
    const sliderPortedQuantity = ref(currentPortedQuantity.value);
    watch(currentPortedQuantity, (v) => {
      sliderPortedQuantity.value = v;
    });
    const sliderNewQuantity = computed(() => totalNumbersQuantity.value - sliderPortedQuantity.value);

    // methods
    // Nur die wachsende Seite wird per addToCart übermittelt, der Server reduziert die andere Seite entsprechend
    const handlePortedChange = async (qty: number) => {
      const diff = qty - currentPortedQuantity.value;
      if (diff > 0) {
        await orderService?.addToCart(portedNumberVariant.value?.id ?? 0, "", diff);
      } else if (diff < 0) {
        await orderService?.addToCart(newNumberVariant.value?.id ?? 0, "", (-1) * diff);
      }
    };
    const handleSliderChange = async () => {
      await handlePortedChange(sliderPortedQuantity.value);
    };

    return {
      t,
      totalNumbersQuantity,
      newNumberVariants,
      portedNumberVariants,
      sliderPortedQuantity,
      sliderNewQuantity,
      handleSliderChange,
      priceFormatter,
      vatType,
    };
  },
} as any);
